import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map } from 'rxjs/operators';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import {
  ContactsService,
  DoctorService,
  PatientsService,
} from '../../../../../../../../core/lists/_services';
import { PractitionRoleService } from '../../../../../../../../core/lists';
import { select, Store } from '@ngrx/store';

import { AppState } from '../../../../../../../../core/reducers';
import {
  ConditionOnServerCreated,
  ConditionOnServerUpdated,
} from '../../../../../../../../core/lists/_actions/condition.actions';
import {
  selectConditionErrorState,
  selectConditionsActionLoading,
} from '../../../../../../../../core/lists/_selectors/condition.selectors';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Condition } from '../../../../../../../../core/lists/_models/condition.model';
import { ConditionsService } from '../../../../../../../../core/lists/_services/condition.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'kt-condition-edit',
  templateUrl: './condition-edit.component.html',
})
export class ConditionEditComponent implements OnInit, OnDestroy {
  // Public properties
  condition: Condition;
  conditionEditForm: FormGroup;
  hasFormErrors = false;
  hasSubmissionErrors = false;
  submissionErrorObj: any;
  viewLoading = true;
  loadingAfterSubmit = false;
  entityInputController;
  selectable = true;
  removable = true;
  filteredEntities: Observable<string[]>;
  entityList: string[] = [];
  @ViewChild('entityInput') entityInput: ElementRef<HTMLInputElement>;
  isCreate: boolean | boolean;

  filteredCode;
  @ViewChild('codeInput') codeInput: ElementRef<HTMLInputElement>;
  codeList: any = [];

  filteredSeverity;
  @ViewChild('severityInput') severityInput: ElementRef<HTMLInputElement>;
  severityList: any = [];

  filteredBodySite;
  @ViewChild('bodySiteInput') bodySiteInput: ElementRef<HTMLInputElement>;
  bodySiteList: any = [];

  filteredEvidence;
  @ViewChild('evidenceInput') evidenceInput: ElementRef<HTMLInputElement>;
  evidenceList: any = [];

  filteredCategory;
  @ViewChild('categoryInput') categoryInput: ElementRef<HTMLInputElement>;
  categoryList: any = [];

  filteredType;
  @ViewChild('typeInput') typeInput: ElementRef<HTMLInputElement>;
  typeList: any = [];

  filteredSummary;
  @ViewChild('summaryInput') summaryInput: ElementRef<HTMLInputElement>;
  summaryList: any = [];

  filteredRecorder;
  @ViewChild('recorderInput') recorderInput: ElementRef<HTMLInputElement>;
  recorderList: any = [];

  filteredAsserter;
  @ViewChild('asserterInput') asserterInput: ElementRef<HTMLInputElement>;
  asserterList: any = [];
  categorys = new FormControl();
  categorys$ = new BehaviorSubject<any>([]);
  private componentSubscriptions: Subscription;
  private patientId: any;
  recorderOption = '';
  asserterOption = '';
  onsetTypeOption = '';
  abatementTypeOption = '';
  public isReadOnly: boolean;

  constructor(
    public dialogRef: MatDialogRef<ConditionEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private store: Store<AppState>,
    private practitionRoleService: PractitionRoleService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private patientsService: PatientsService,
    private doctorsService: DoctorService,
    private conditionService: ConditionsService,
    private contactsService: ContactsService,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.isReadOnly = this.data.isReadOnly;

    this.condition = this.data.condition;
    this.patientId = this.data.entityId;
    this.store
      .pipe(select(selectConditionsActionLoading))
      .subscribe((res) => (this.viewLoading = res));
    this.createForm();
  }

  ngOnDestroy() {
    if (this.componentSubscriptions) {
      this.componentSubscriptions.unsubscribe();
    }
  }

  createForm() {
    let onsetType = '';
    let onsetDateVal = '';
    let onsetStringVal = '';
    let onsetPeriodStartVal = '';
    let onsetPeriodEndVal = '';
    let onsetAgeVal = '';
    let onsetAgeUnitVal = '';
    let onsetRangeHighVal = '';
    let onsetRangeLowVal = '';
    const objOnset = this.condition.onset;
    if (objOnset !== null && objOnset !== undefined) {
      const keys = Object.keys(objOnset);
      switch (keys[0]) {
        case 'dateTime': {
          onsetType = 'dateTime';
          this.onsetTypeOption = onsetType;
          onsetDateVal = this.condition.onset['dateTime'];
          break;
        }
        case 'string': {
          onsetType = 'string';
          this.onsetTypeOption = onsetType;
          onsetStringVal = this.condition.onset['string'];
          break;
        }
        case 'Period': {
          onsetType = 'Period';
          this.onsetTypeOption = onsetType;
          onsetPeriodStartVal = this.condition.onset['Period']['start'];
          onsetPeriodEndVal = this.condition.onset['Period']['end'];
          break;
        }
        case 'Age': {
          onsetType = 'Age';
          this.onsetTypeOption = onsetType;
          onsetAgeVal = this.condition.onset['Age']['value'];
          onsetAgeUnitVal = this.condition.onset['Age']['unit'];
          break;
        }
        case 'Range': {
          onsetType = 'Range';
          this.onsetTypeOption = onsetType;
          onsetRangeLowVal = this.condition.onset['Range']['low']['value'];
          onsetRangeHighVal = this.condition.onset['Range']['high']['value'];
          break;
        }
        default: {
          break;
        }
      }
    }

    let abatementType = '';
    let abatementDateVal = '';
    let abatementStringVal = '';
    let abatementPeriodStartVal = '';
    let abatementPeriodEndVal = '';
    let abatementAgeVal = '';
    let abatementAgeUnitVal = '';
    let abatementRangeHighVal = '';
    let abatementRangeLowVal = '';
    const objAbatement = this.condition.abatement;
    if (objAbatement !== null && objAbatement !== undefined) {
      const keys = Object.keys(objAbatement);
      switch (keys[0]) {
        case 'dateTime': {
          abatementType = 'dateTime';
          this.abatementTypeOption = abatementType;
          abatementDateVal = this.condition.abatement['dateTime'];
          break;
        }
        case 'string': {
          abatementType = 'string';
          this.abatementTypeOption = abatementType;
          abatementStringVal = this.condition.abatement['string'];
          break;
        }
        case 'Period': {
          abatementType = 'Period';
          this.abatementTypeOption = abatementType;
          abatementPeriodStartVal = this.condition.abatement['Period']['start'];
          abatementPeriodEndVal = this.condition.abatement['Period']['end'];
          break;
        }
        case 'Age': {
          abatementType = 'Age';
          this.abatementTypeOption = abatementType;
          abatementAgeVal = this.condition.abatement['Age']['value'];
          abatementAgeUnitVal = this.condition.abatement['Age']['unit'];
          break;
        }
        case 'Range': {
          abatementType = 'Range';
          this.abatementTypeOption = abatementType;
          abatementRangeLowVal = this.condition.abatement['Range']['low']['value'];
          abatementRangeHighVal = this.condition.abatement['Range']['high']['value'];
          break;
        }
        default: {
          break;
        }
      }
    }

    this.recorderOption =
      this.condition.recorder !== null && this.condition.recorder !== undefined
        ? this.condition.recorder.resourceType
        : '';

    this.asserterOption =
      this.condition.asserter !== null && this.condition.asserter !== undefined
        ? this.condition.asserter.resourceType
        : '';

    if (
      this.condition &&
      this.condition.recorder &&
      this.condition.recorder['id'] &&
      this.condition.recorder['resourceType']
    ) {
      this.conditionService
        .getResourceById(this.condition.recorder['resourceType'], this.condition.recorder['id'])
        .subscribe((res) => {
          if (res) {
            this.conditionEditForm.controls.recorder.setValue(res);
          }
        });
    }

    if (
      this.condition &&
      this.condition.asserter &&
      this.condition.asserter['id'] &&
      this.condition.asserter['resourceType']
    ) {
      this.conditionService
        .getResourceById(this.condition.asserter['resourceType'], this.condition.asserter['id'])
        .subscribe((res) => {
          if (res) {
            this.conditionEditForm.controls.asserter.setValue(res);
          }
        });
    }

    this.conditionEditForm = this.fb.group({
      clinicalStatus: [
        this.condition.id && this.condition.clinicalStatus !== null
          ? this.condition.clinicalStatus.coding[0].code
          : null,
      ],

      verificationStatus: [
        this.condition.id && this.condition.verificationStatus !== null
          ? this.condition.verificationStatus.coding[0].code
          : null,
      ],

      code: [undefined, Validators.required],
      severity: [],
      type: [],
      bodySite: [],
      summary: [],
      evidence: [],

      recordedDate: [
        this.condition.recordedDate !== null && this.condition.recordedDate !== ''
          ? new Date(this.condition.recordedDate)
          : '',
      ],

      recorderResourceType: [
        this.condition.recorder !== null && this.condition.recorder !== undefined
          ? this.condition.recorder.resourceType
          : '',
      ],

      recorder: [],

      asserterType: [
        this.condition.asserter !== null && this.condition.asserter !== undefined
          ? this.condition.asserter.resourceType
          : '',
      ],

      asserter: [],

      note: [
        this.condition.note !== null && this.condition.note !== undefined
          ? this.condition.note[0].text
          : '',
        Validators.required,
      ],

      onset_type: [onsetType],
      onsetDate: [onsetDateVal],
      onsetString: [onsetStringVal],
      onsetPeriodStart: [onsetPeriodStartVal],
      onsetPeriodEnd: [onsetPeriodEndVal],
      onsetAge: [onsetAgeVal],
      onsetAgeUnit: [onsetAgeUnitVal],
      onsetRangeHigh: [onsetRangeHighVal],
      onsetRangeLow: [onsetRangeLowVal],
      abatement_type: [abatementType],
      abatementDate: [abatementDateVal],
      abatementString: [abatementStringVal],
      abatementPeriodStart: [abatementPeriodStartVal],
      abatementPeriodEnd: [abatementPeriodEndVal],
      abatementAge: [abatementAgeVal],
      abatementAgeUnit: [abatementAgeUnitVal],
      abatementRangeHigh: [abatementRangeHighVal],
      abatementRangeLow: [abatementRangeLowVal],

      checkArray: this.fb.array([this.condition.category]),
    });

    if (this.isReadOnly) {
      this.conditionEditForm.disable();
    }
  }

  prepareCondition(): Condition {
    const controls = this.conditionEditForm.controls;
    const _condition = new Condition();
    _condition.clear();
    _condition.id = this.condition.id;
    _condition.code = controls.code.value;
    _condition.clinicalStatus = controls.clinicalStatus.value;
    _condition.verificationStatus = controls.verificationStatus.value;
    _condition.recordedDate = this.datePipe.transform(controls.recordedDate.value, 'yyyy-MM-dd');
    _condition.note = controls.note.value;
    _condition.type = controls.type.value;
    _condition.summary = controls.summary.value;
    _condition.evidence = controls.evidence.value;
    _condition.severity = controls.severity.value;
    _condition.bodySite = controls.bodySite.value;
    _condition.recorderResourceType = controls.recorderResourceType.value;
    _condition.recorder = controls.recorder.value;
    _condition.onsetType = controls.onset_type.value;
    _condition.onsetDate = this.datePipe.transform(controls.onsetDate.value, 'yyyy-MM-dd');
    _condition.onsetString = controls.onsetString.value;
    _condition.onsetPeriodStart = this.datePipe.transform(
      controls.onsetPeriodStart.value,
      'yyyy-MM-dd',
    );
    _condition.onsetPeriodEnd = this.datePipe.transform(
      controls.onsetPeriodEnd.value,
      'yyyy-MM-dd',
    );
    _condition.onsetAge = controls.onsetAge.value;
    _condition.category = this.categorys.value;
    _condition.onsetAgeUnit = controls.onsetAgeUnit.value;
    _condition.onsetRangeLow = controls.onsetRangeLow.value;
    _condition.onsetRangeHigh = controls.onsetRangeHigh.value;
    _condition.abatementType = controls.abatement_type.value;
    _condition.abatementDate = this.datePipe.transform(controls.abatementDate.value, 'yyyy-MM-dd');
    _condition.abatementString = controls.abatementString.value;
    _condition.abatementPeriodStart = this.datePipe.transform(
      controls.abatementPeriodStart.value,
      'yyyy-MM-dd',
    );
    _condition.abatementPeriodEnd = this.datePipe.transform(
      controls.abatementPeriodEnd.value,
      'yyyy-MM-dd',
    );
    _condition.abatementAge = controls.abatementAge.value;
    _condition.abatementAgeUnit = controls.abatementAgeUnit.value;
    _condition.abatementRangeLow = controls.abatementRangeLow.value;
    _condition.abatementRangeHigh = controls.abatementRangeHigh.value;
    _condition.asserter = controls.asserter.value;
    _condition.asserterType = controls.asserterType.value;
    return _condition;
  }

  onSubmit() {
    this.hasFormErrors = false;
    this.hasSubmissionErrors = false;
    const controls = this.conditionEditForm.controls;
    /** check form */
    if (this.conditionEditForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());

      this.hasFormErrors = true;
      return;
    }

    const editedCondition = this.prepareCondition();
    if (editedCondition.id !== 0) {
      this.updateCondition(editedCondition);
    } else {
      this.createCondition(editedCondition);
    }
  }

  updateCondition(_condition: Condition) {
    this.store.dispatch(
      new ConditionOnServerUpdated({
        condition: _condition,
        entityId: this.patientId,
      }),
    );
    this.store.pipe(select(selectConditionsActionLoading)).subscribe((res) => {
      if (res !== true) {
        this.store.pipe(select(selectConditionErrorState)).subscribe((error) => {
          if (error) {
            if (error.error.errors) {
              this.submissionErrorObj = error.error.errors;
            } else {
              this.submissionErrorObj = [{ message: 'GLOBAL.UNSPECIFIED_ERROR' }];
            }
            this.hasSubmissionErrors = true;
          } else {
            this.dialogRef.close({ _condition, isEdit: true });
          }
        });
      }
    });
  }

  createCondition(_condition: Condition) {
    this.store.dispatch(
      new ConditionOnServerCreated({ condition: _condition, entityId: this.patientId }),
    );
    this.store.pipe(select(selectConditionsActionLoading)).subscribe((res) => {
      if (res !== true) {
        this.store.pipe(select(selectConditionErrorState)).subscribe((error) => {
          if (error) {
            if (error.error.errors) {
              this.submissionErrorObj = error.error.errors;
            } else {
              this.submissionErrorObj = [{ message: 'GLOBAL.UNSPECIFIED_ERROR' }];
            }
            this.hasSubmissionErrors = true;
          } else {
            this.dialogRef.close({ _condition, isEdit: false });
          }
        });
      }
    });
  }

  onAlertClose() {
    this.hasFormErrors = false;
  }

  getTitle() {
    let result = this.translate.instant('CONDITION.NEW_CONDITION');
    if (!this.condition.id) {
      return result;
    }
    if (this.isReadOnly) {
      result = this.translate.instant('CONDITION.VIEW_CONDITION') + `-` + `${this.condition.id}`;
      return result;
    }
    result = this.translate.instant('CONDITION.EDIT_CONDITION') + `-` + `${this.condition.id}`;
    return result;
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.conditionEditForm.controls[controlName];
    if (!control) {
      return false;
    }

    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }

  selectedCode(event: MatAutocompleteSelectedEvent): void {
    this.codeList.push(event.option.value);
    this.codeInput.nativeElement.value = event.option.value.concept.fsn.term;
  }

  codeInputChanged(value) {
    if (value) {
      if (typeof value === 'string')
        this.filteredCode = this.practitionRoleService.searchCode(value).pipe(
          map((res) =>
            res.items.filter((r) => {
              return !this.codeList.find((e: any) => e.concept.id === r.concept.id);
            }),
          ),
        );
    } else {
      this.filteredCode = of([]);
    }
  }

  displayFnCode(event: any): string {
    return event && event.concept.fsn.term ? event.concept.fsn.term : '';
  }

  selectedSummary(event: MatAutocompleteSelectedEvent): void {
    this.summaryList.push(event.option.value);
    this.summaryInput.nativeElement.value = event.option.value.concept.fsn.term;
  }

  summaryInputChanged(value) {
    if (value) {
      if (typeof value === 'string')
        this.filteredSummary = this.practitionRoleService.searchCode(value).pipe(
          map((res) =>
            res.items.filter((r) => {
              return !this.summaryList.find((e: any) => e.concept.id === r.concept.id);
            }),
          ),
        );
    } else {
      this.filteredSummary = of([]);
    }
  }

  displayFnSummary(event: any): string {
    return event && event.concept.fsn.term ? event.concept.fsn.term : '';
  }

  selectedCategory(event: MatAutocompleteSelectedEvent): void {
    this.categoryList.push(event.option.value);
    this.categoryInput.nativeElement.value = event.option.value.concept.fsn.term;
  }

  categoryInputChanged(value) {
    if (value) {
      if (typeof value === 'string')
        this.filteredCategory = this.practitionRoleService.searchCode(value).pipe(
          map((res) =>
            res.items.filter((r) => {
              return !this.categoryList.find((e: any) => e.concept.id === r.concept.id);
            }),
          ),
        );
    } else {
      this.filteredCategory = of([]);
    }
  }

  displayFnCategory(event: any): string {
    return event && event.concept.fsn.term ? event.concept.fsn.term : '';
  }

  selectedType(event: MatAutocompleteSelectedEvent): void {
    this.typeList.push(event.option.value);
    this.typeInput.nativeElement.value = event.option.value.concept.fsn.term;
  }

  typeInputChanged(value) {
    if (value) {
      if (typeof value === 'string')
        this.filteredType = this.practitionRoleService.searchCode(value).pipe(
          map((res) =>
            res.items.filter((r) => {
              return !this.typeList.find((e: any) => e.concept.id === r.concept.id);
            }),
          ),
        );
    } else {
      this.filteredType = of([]);
    }
  }

  displayFnType(event: any): string {
    return event && event.concept.fsn.term ? event.concept.fsn.term : '';
  }

  selectedEvidence(event: MatAutocompleteSelectedEvent): void {
    this.evidenceList.push(event.option.value);
    this.evidenceInput.nativeElement.value = event.option.value.concept.fsn.term;
  }

  evidenceInputChanged(value) {
    if (value) {
      if (typeof value === 'string')
        this.filteredEvidence = this.practitionRoleService.searchCode(value).pipe(
          map((res) =>
            res.items.filter((r) => {
              return !this.evidenceList.find((e: any) => e.concept.id === r.concept.id);
            }),
          ),
        );
    } else {
      this.filteredEvidence = of([]);
    }
  }

  displayFnEvidence(event: any): string {
    return event && event.concept.fsn.term ? event.concept.fsn.term : '';
  }

  onCatChange(e) {
    const checkArray: FormArray = this.conditionEditForm.get('checkArray') as FormArray;

    if (e.target.checked) {
      checkArray.push(new FormControl(e.target.value));
    } else {
      let i = 0;
      checkArray.controls.forEach((item: FormControl) => {
        if (item.value === e.target.value) {
          checkArray.removeAt(i);
          return;
        }
        i++;
      });
    }
  }

  selectedRecorder(event: MatAutocompleteSelectedEvent): void {
    this.recorderList.push(event.option.value);
    if (this.recorderOption === 'RelatedPerson') {
      this.recorderInput.nativeElement.value =
        event.option.value && event.option.value.contact
          ? event.option.value.contact.first_name
          : null;
      return;
    }
    this.recorderInput.nativeElement.value = event.option.value.first_name;
  }

  recorderInputChanged(value, typeOfSelected) {
    if (
      value &&
      (typeOfSelected === 'Patient' ||
        typeOfSelected === 'Practitioner' ||
        typeOfSelected === 'RelatedPerson')
    ) {
      if (typeof value === 'string' && typeOfSelected === 'Patient')
        this.filteredRecorder = this.patientsService.searchPatients(value).pipe(
          map((res) =>
            res.data.filter((r) => {
              return !this.recorderList.find((e: any) => e.id === r.id);
            }),
          ),
        );
      if (typeof value === 'string' && typeOfSelected === 'Practitioner')
        this.filteredRecorder = this.doctorsService.searchDoctors(value).pipe(
          map((res) =>
            res.data.filter((r) => {
              return !this.recorderList.find((e: any) => e.id === r.id);
            }),
          ),
        );
      if (typeof value === 'string' && typeOfSelected === 'RelatedPerson')
        this.filteredRecorder = this.contactsService
          .searchRelatedPerson(value, this.patientId)
          .pipe(
            map((res) =>
              res.data.filter((r) => {
                return !this.recorderList.find((e: any) => e.id === r.id);
              }),
            ),
          );
    } else {
      this.filteredRecorder = of([]);
    }
  }

  displayFnRecorder(event: any): string {
    if (this.recorderOption === 'RelatedPerson') {
      return event && event['patient'] ? event['patient']['resourceType'] : null;
    }
    return event && event['name'] && event['name'][0] && event['name'][0]['given']
      ? event['name'][0]['given'][0]
      : null;
  }

  choseRecorderType(value) {
    this.recorderOption = value;
    this.recorderList = [];
    this.filteredRecorder = of([]);
    if (this.recorderInput) this.recorderInput.nativeElement.value = null;
  }

  chooseOnsetType(value) {
    this.onsetTypeOption = value;
    // this.recorderList = [];
    // this.recorderInput.nativeElement.value = null;
  }

  chooseAbatementType(value) {
    this.abatementTypeOption = value;
    // this.recorderList = [];
    // this.recorderInput.nativeElement.value = null;
  }

  selectedAsserter(event: MatAutocompleteSelectedEvent): void {
    this.asserterList.push(event.option.value);
    if (this.asserterOption === 'RelatedPerson') {
      this.asserterInput.nativeElement.value =
        event.option.value && event.option.value.contact
          ? event.option.value.contact.first_name
          : null;
      return;
    }
    this.asserterInput.nativeElement.value = event.option.value.first_name;
  }

  asserterInputChanged(value, typeOfSelected) {
    if (value) {
      if (typeof value === 'string' && typeOfSelected === 'Patient')
        this.filteredAsserter = this.patientsService.searchPatients(value).pipe(
          map((res) =>
            res.data.filter((r) => {
              return !this.asserterList.find((e: any) => e.id === r.id);
            }),
          ),
        );
      if (typeof value === 'string' && typeOfSelected === 'Practitioner')
        this.filteredAsserter = this.doctorsService.searchDoctors(value).pipe(
          map((res) =>
            res.data.filter((r) => {
              return !this.asserterList.find((e: any) => e.id === r.id);
            }),
          ),
        );
      if (typeof value === 'string' && typeOfSelected === 'RelatedPerson')
        this.filteredAsserter = this.contactsService
          .searchRelatedPerson(value, this.patientId)
          .pipe(
            map((res) =>
              res.data.filter((r) => {
                return !this.asserterList.find((e: any) => e.id === r.id);
              }),
            ),
          );
    } else {
      this.filteredAsserter = of([]);
    }
  }

  displayFnAsserter(event: any): string {
    if (this.asserterOption === 'RelatedPerson') {
      return event && event['patient'] ? event['patient']['resourceType'] : null;
    }
    return event && event['name'] && event['name'][0] && event['name'][0]['given']
      ? event['name'][0]['given'][0]
      : null;
  }

  chooseAsserterType(value) {
    this.asserterOption = value;
    this.asserterList = [];
    this.filteredAsserter = of([]);
    if (this.asserterInput) this.asserterInput.nativeElement.value = null;
  }

  selectedSeverity(event: MatAutocompleteSelectedEvent): void {
    this.severityList.push(event.option.value);
    this.severityInput.nativeElement.value = event.option.value.concept.fsn.term;
  }

  severityInputChanged(value) {
    if (value) {
      if (typeof value === 'string')
        this.filteredSeverity = this.practitionRoleService.searchCode(value).pipe(
          map((res) =>
            res.items.filter((r) => {
              return !this.severityList.find((e: any) => e.concept.id === r.concept.id);
            }),
          ),
        );
    } else {
      this.filteredSeverity = of([]);
    }
  }

  displayFnSeverity(event: any): string {
    return event && event.concept.fsn.term ? event.concept.fsn.term : '';
  }

  selectedBodySite(event: MatAutocompleteSelectedEvent): void {
    this.bodySiteList.push(event.option.value);
    this.bodySiteInput.nativeElement.value = event.option.value.concept.fsn.term;
  }

  bodySiteInputChanged(value) {
    if (value) {
      if (typeof value === 'string')
        this.filteredBodySite = this.practitionRoleService.searchCode(value).pipe(
          map((res) =>
            res.items.filter((r) => {
              return !this.bodySiteList.find((e: any) => e.concept.id === r.concept.id);
            }),
          ),
        );
    } else {
      this.filteredBodySite = of([]);
    }
  }

  displayFnBodySite(event: any): string {
    return event && event.concept.fsn.term ? event.concept.fsn.term : '';
  }

  private loadCategorys() {
    // this.affiliationsService.getAffiliationsForViewInPatient(this.patient.id, tagsViewContext.ENTITIES).subscribe((affiliations) => {
    //   this.affiliation_groups$.next(affiliations.data);
    // }, () => {
    // })
  }

  getName(obj, type) {
    if (type === 'RelatedPerson' && obj.contact) {
      return obj.contact.first_name + ' ' + obj.contact.last_name;
    }
    return obj.first_name + ' ' + obj.last_name;
  }
}
